import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, CheckCircle2, AlertCircle, ChevronRight, ChevronLeft } from 'lucide-react';
import { usePublicForm } from '../../hooks/useForms';
import { Button } from '../Button';
import { Reveal } from '../Reveal';

import { useConditionalLogic } from '../../hooks/useConditionalLogic';
import { useFormScoring } from '../../hooks/useFormScoring';
import { FormTypeform } from './FormTypeform';
import { FormLogo } from './FormLogo';
import { ScoreResult } from './ScoreResult';
import { OneFormBadge } from './OneFormBadge';
import { getAlignmentClass, getScreenAlignmentClass } from '../../lib/getAlignmentClass';
import {
  TextField,
  TextAreaField,
  SelectField,
  CheckboxField,
  RadioField,
  DateField,
  FileUploadField,
  OpinionScaleField,
  NPSField,
  RatingField,
  PictureChoiceField,
  YesNoField,
  StatementField,
  WelcomeScreenField,
  ThankYouScreenField,
} from './fields';
import type { FormField } from '../../types/forms';
import { NON_INPUT_FIELD_TYPES, DEFAULT_THEME_CONFIG } from '../../types/forms';
import { getButtonStyles } from '../../lib/themeUtils';
import { useThemeFont } from '../../hooks/useThemeFont';

export const FormPublic: React.FC = () => {
  const { formId } = useParams<{ formId: string }>();
  const { form, loading, error, submitResponse } = usePublicForm(formId);

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Conditional logic: filter visible fields based on formData (show/hide)
  const { visibleFields, ruleCalculations } = useConditionalLogic({
    fields: form?.fields || [],
    formData,
  });

  // Scoring engine (includes rule-based calculations)
  const { scores, isEnabled: scoringEnabled } = useFormScoring({
    fields: form?.fields || [],
    formData,
    scoringConfig: form?.settings?.scoring,
    ruleCalculations,
  });

  const themeConfig = form?.settings?.themeConfig || DEFAULT_THEME_CONFIG;
  useThemeFont(themeConfig.fontFamily);

  // Agrupar campos por bloco para multi-step
  const getSteps = () => {
    if (!form?.fields) return [];

    const blocks: { name: string; fields: FormField[] }[] = [];
    let currentBlock: { name: string; fields: FormField[] } = { name: 'Informações', fields: [] };

    visibleFields.forEach((field) => {
      if (field.type === 'heading') {
        if (currentBlock.fields.length > 0) {
          blocks.push(currentBlock);
        }
        currentBlock = { name: field.label, fields: [] };
      } else {
        currentBlock.fields.push(field);
      }
    });

    if (currentBlock.fields.length > 0) {
      blocks.push(currentBlock);
    }

    // Se não há blocos, retorna todos os campos em um único step
    return blocks.length > 0 ? blocks : [{ name: 'Formulário', fields: visibleFields }];
  };

  const steps = getSteps();
  const currentFields = steps[currentStep]?.fields || [];
  const isLastStep = currentStep === steps.length - 1;
  const progress = ((currentStep + 1) / steps.length) * 100;

  // Atualizar valor do campo
  const handleFieldChange = (fieldId: string, value: any) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));

    // Limpar erro ao editar
    if (errors[fieldId]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
    }
  };

  // Validar campos do step atual
  const validateCurrentStep = (): boolean => {
    const newErrors: Record<string, string> = {};

    currentFields.forEach((field) => {
      // Skip validation for non-input fields
      if (NON_INPUT_FIELD_TYPES.includes(field.type)) return;

      if (field.required) {
        const value = formData[field.id];

        if (!value || (Array.isArray(value) && value.length === 0)) {
          newErrors[field.id] = 'Este campo é obrigatório';
        }
      }

      // Validações específicas
      if (field.type === 'email' && formData[field.id]) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData[field.id])) {
          newErrors[field.id] = 'Email inválido';
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Próximo step
  const handleNext = () => {
    if (!validateCurrentStep()) return;

    if (isLastStep) {
      handleSubmit();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  // Step anterior
  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(0, prev - 1));
  };

  // Enviar formulário
  const handleSubmit = async () => {
    if (!validateCurrentStep()) return;

    setIsSubmitting(true);
    const result = await submitResponse(formData, scores);
    setIsSubmitting(false);

    if (result.error) {
      setErrors({ _form: result.error });
      return;
    }

    // Redirecionar ou mostrar mensagem de sucesso
    if (form?.redirect_url) {
      try {
        const url = new URL(form.redirect_url);
        if (url.protocol === 'https:' || url.protocol === 'http:') {
          window.location.href = form.redirect_url;
        } else {
          setIsSubmitted(true);
        }
      } catch {
        setIsSubmitted(true);
      }
    } else {
      setIsSubmitted(true);
    }
  };

  // Renderizar campo
  const renderField = (field: FormField) => {
    const value = formData[field.id];
    const error = errors[field.id];
    const onChange = (newValue: any) => handleFieldChange(field.id, newValue);

    const commonProps = { field, value, onChange, error, themeConfig };

    switch (field.type) {
      case 'text':
      case 'email':
      case 'phone':
      case 'url':
      case 'number':
        return <TextField {...commonProps} />;
      case 'textarea':
        return <TextAreaField {...commonProps} />;
      case 'select':
        return <SelectField {...commonProps} />;
      case 'checkbox':
        return <CheckboxField {...commonProps} value={value || []} />;
      case 'radio':
        return <RadioField {...commonProps} />;
      case 'date':
      case 'datetime':
      case 'time':
        return <DateField {...commonProps} />;
      case 'file':
        return <FileUploadField {...commonProps} />;
      case 'paragraph':
        return <p style={{ color: themeConfig.textColor, opacity: 0.6 }}>{field.placeholder}</p>;
      case 'opinion_scale':
        return <OpinionScaleField {...commonProps} />;
      case 'nps':
        return <NPSField {...commonProps} />;
      case 'rating':
        return <RatingField {...commonProps} />;
      case 'picture_choice':
        return <PictureChoiceField {...commonProps} />;
      case 'yes_no':
        return <YesNoField {...commonProps} />;
      case 'statement':
        return <StatementField {...commonProps} />;
      case 'welcome_screen':
        return <WelcomeScreenField {...commonProps} />;
      case 'thank_you_screen':
        return <ThankYouScreenField {...commonProps} />;
      default:
        return <TextField {...commonProps} />;
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="h-dvh bg-neutral-950 flex items-center justify-center overflow-hidden">
        <Loader2 className="w-10 h-10 text-primary-500 animate-spin" />
      </div>
    );
  }

  // Form owner inactive (subscription expired)
  if (error === 'form_owner_inactive') {
    return (
      <div className="h-dvh bg-neutral-950 flex flex-col items-center justify-center p-4 overflow-hidden">
        <AlertCircle className="w-16 h-16 text-neutral-500 mb-4" />
        <h1 className="text-xl font-bold text-white mb-2">Formulario indisponivel</h1>
        <p className="text-neutral-400 text-center max-w-md">
          Este formulario esta temporariamente fora do ar. Entre em contato com o responsavel.
        </p>
      </div>
    );
  }

  // Error state
  if (error || !form) {
    return (
      <div className="h-dvh bg-neutral-950 flex flex-col items-center justify-center p-4 overflow-hidden">
        <AlertCircle className="w-16 h-16 text-red-400 mb-4" />
        <h1 className="text-xl font-bold text-white mb-2">Formulário não encontrado</h1>
        <p className="text-neutral-400 text-center">
          Este formulário pode ter sido removido ou não está mais disponível.
        </p>
      </div>
    );
  }

  // Mode detection: delegate to typeform renderer if displayMode is 'typeform'
  const displayMode = form.settings?.displayMode || 'typeform';
  if (displayMode === 'typeform') {
    return <FormTypeform form={form} onSubmit={submitResponse} />;
  }

  // Success state
  if (isSubmitted) {
    const thankYouField = form?.fields.find((f) => f.type === 'thank_you_screen');
    return (
      <div className="h-dvh flex flex-col items-center justify-center p-4 overflow-hidden" style={{ backgroundColor: themeConfig.backgroundColor }}>
        <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
          <div
            className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-green-600/10 rounded-full blur-[100px] mix-blend-screen"
          />
        </div>

        <Reveal variant="zoom-in">
          <div className={`relative z-10 liquid-glass rounded-3xl p-10 max-w-md ${getAlignmentClass(thankYouField?.alignment ?? 'center')}`}>
            <div className="w-20 h-20 mx-auto bg-green-500/20 rounded-full flex items-center justify-center mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-400" />
            </div>
            <h1 className="text-2xl font-bold mb-4" style={{ color: themeConfig.textColor }}>Enviado com sucesso!</h1>
            <p style={{ color: themeConfig.textColor, opacity: 0.6 }}>
              {form.thank_you_message || 'Obrigado por enviar o formulário!'}
            </p>
            {scoringEnabled && form?.settings?.scoring?.showResultToRespondent && scores && (
              <ScoreResult
                scores={scores}
                variables={form.settings.scoring.variables}
              />
            )}
          </div>
        </Reveal>
      </div>
    );
  }

  // Background image do campo atualmente renderizado (pega do 1º campo do step) — Story saas-1.6
  const primaryBgField = currentFields.find(f => f.config?.backgroundImage);
  const primaryBg = primaryBgField?.config?.backgroundImage;
  const primaryBgUrl = (() => {
    if (!primaryBg) return null;
    try {
      const parsed = new URL(primaryBg);
      if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
      return encodeURI(primaryBg);
    } catch {
      return null;
    }
  })();
  const primaryBgBlur = primaryBgField?.config?.backgroundBlur ?? 0;
  const primaryBgOpacity = primaryBgField?.config?.backgroundOpacity ?? 100;

  return (
    <div className="h-dvh relative overflow-y-auto" style={{ backgroundColor: themeConfig.backgroundColor, fontFamily: `'${themeConfig.fontFamily}', sans-serif` }}>
      {/* Camada absoluta de fundo — filtros aplicam apenas aqui (Story saas-1.6) */}
      {primaryBgUrl && (
        <div
          className="fixed inset-0 z-0 pointer-events-none"
          style={{
            backgroundImage: `url("${primaryBgUrl}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: primaryBgBlur > 0 ? `blur(${primaryBgBlur}px)` : undefined,
            opacity: primaryBgOpacity / 100,
          }}
        />
      )}

      {/* Background blob */}
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-[-10%] left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-[100px] mix-blend-screen"
        />
      </div>

      {/* Logo absolute (6 posições) */}
      <FormLogo
        logoUrl={form.settings?.logoUrl}
        logoPosition={form.settings?.logoPosition}
        logoMargin={form.settings?.logoMargin}
        logoScale={form.settings?.logoScale}
        variant="absolute"
      />

      {/* Content */}
      <main className="relative z-10 px-4 py-6 pb-20 max-w-2xl mx-auto">
        <Reveal variant="fade-up">
          {/* Progress Bar */}
          {form.settings?.showProgressBar !== false && steps.length > 1 && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-neutral-400">
                  Passo {currentStep + 1} de {steps.length}
                </span>
                <span className="text-sm text-neutral-400">
                  {Math.round(progress)}%
                </span>
              </div>
              <div className="h-2 bg-neutral-800 rounded-full overflow-hidden">
                <div
                  className="h-full transition-all duration-300"
                  style={{ width: `${progress}%`, backgroundColor: themeConfig.buttonBgColor }}
                />
              </div>
            </div>
          )}

          {/* Form Card */}
          <div className="liquid-glass rounded-3xl p-8 md:p-10">
            {/* Logo inline — apenas middle-center, sempre acima da pergunta */}
            <FormLogo
              logoUrl={form.settings?.logoUrl}
              logoPosition={form.settings?.logoPosition}
              logoMargin={form.settings?.logoMargin}
              logoScale={form.settings?.logoScale}
              variant="inline"
            />

            {/* Title */}
            <h1 className="text-2xl font-bold mb-2" style={{ color: themeConfig.textColor }}>{form.title}</h1>
            {form.description && (
              <p className="mb-8" style={{ color: themeConfig.textColor, opacity: 0.6 }}>{form.description}</p>
            )}

            {/* Step Title */}
            {steps.length > 1 && (
              <h2 className="text-lg font-semibold mb-6" style={{ color: themeConfig.textColor }}>
                {steps[currentStep].name}
              </h2>
            )}

            {/* Error global */}
            {errors._form && (
              <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {errors._form}
              </div>
            )}

            {/* Fields */}
            <div className="space-y-6">
              {currentFields.map((field, index) => (
                <Reveal key={field.id} variant="fade-up" delay={index * 50}>
                  <div className={getScreenAlignmentClass(field.type, field.alignment)}>
                    {!NON_INPUT_FIELD_TYPES.includes(field.type) && (
                      <label className="block text-sm font-medium mb-2" style={{ color: themeConfig.textColor }}>
                        {field.label}
                        {field.required && <span className="text-red-400 ml-1">*</span>}
                      </label>
                    )}
                    {renderField(field)}
                    {field.helpText && (
                      <p className="mt-2 text-sm" style={{ color: themeConfig.textColor, opacity: 0.5 }}>{field.helpText}</p>
                    )}
                    {errors[field.id] && (
                      <p className="mt-2 text-sm text-red-400">{errors[field.id]}</p>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-neutral-700/50">
              {currentStep > 0 ? (
                <Button variant="outline" onClick={handlePrevious}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Voltar
                </Button>
              ) : (
                <div />
              )}

              {/* mobile-ui.3.C AC6: !text-base fica (16px anti-zoom iOS); !px-0 !py-0 removidos — zeravam o padding do getButtonStyles */}
              <Button
                variant="primary"
                onClick={handleNext}
                disabled={isSubmitting}
                style={getButtonStyles(themeConfig, 'primary', 'md')}
                className="!text-base"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : isLastStep ? (
                  'Enviar'
                ) : (
                  <>
                    Próximo
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </Reveal>

        {/* Branding — BP-6 OneFormBadge com gate por plano */}
        <OneFormBadge
          ownerPlanTier={form.owner_plan_tier}
          formSettings={form.settings}
        />
      </main>
    </div>
  );
};
